/**
 * Tag-listing copy helpers shared between the tag route `head()` functions
 * and the TagListingView render surface. Keep the edge-case rules in one
 * place so head() (SEO) and the hero (UI) stay in sync.
 */

const FIELD_NAME_MAP: Record<string, string> = {
  Engineers: "Engineering",
  Analysts: "Analytics",
  Scientists: "Science",
  Architects: "Architecture",
  Managers: "Management",
  Administrators: "Administration",
};

/**
 * Convert a position-group plural tag name to its field noun.
 * e.g. "Data Scientists" → "Data Science", "Analysts" → "Analytics".
 * Without this, tag pages render "Data Scientists jobs" / "Analysts jobs".
 */
export function toFieldName(name: string): string {
  if (name === "Other Positions") return "Other";
  for (const [person, field] of Object.entries(FIELD_NAME_MAP)) {
    if (name.endsWith(person)) {
      return name.slice(0, -person.length) + field;
    }
  }
  return name;
}

// "Staff+" / "Manager+" should keep their capital letter when lowercased.
function smartLower(name: string): string {
  return name.endsWith("+") ? name : name.toLowerCase();
}

interface TagForCopy {
  name: string;
  category?: { slug: string } | null;
}

interface CopyOptions {
  isRemote?: boolean;
}

/** Display name used in every piece of page copy (post position-group fix). */
export function getTagDisplayName(tag: TagForCopy): string {
  if (tag.category?.slug === "position-group") return toFieldName(tag.name);
  return tag.name;
}

/** Page <title>. */
export function getTagMetaTitle(
  tag: TagForCopy,
  options: CopyOptions = {},
): string {
  const name = getTagDisplayName(tag);
  return options.isRemote
    ? `Remote ${name} Jobs | Data First Jobs`
    : `${name} Jobs | Data First Jobs`;
}

/** Schema.org ItemList / breadcrumb name. */
export function getTagListingName(
  tag: TagForCopy,
  options: CopyOptions = {},
): string {
  const name = getTagDisplayName(tag);
  return options.isRemote ? `Remote ${name} Jobs` : `${name} Jobs`;
}

/** Subtitle shown under the H1. */
export function getTagSubtitle(
  tag: TagForCopy,
  options: CopyOptions = {},
): string {
  const name = getTagDisplayName(tag);
  const catSlug = tag.category?.slug;
  const isRemote = !!options.isRemote;

  // When the tag itself already implies remote, drop "and remote" so the
  // sentence doesn't read "Every Remote role tagged by … and remote".
  const tail =
    catSlug === "work-options" || isRemote
      ? "tagged by stack and seniority, so the right move is easier to spot."
      : "tagged by stack, seniority, and remote, so the right move is easier to spot.";

  // Job-type tags (Contract, Full Time, Internship) are generic terms; pin
  // them to the site's domain with "data role".
  if (catSlug === "job-type") {
    const lower = name.toLowerCase();
    const prefix = isRemote
      ? `Every remote ${lower} data role`
      : `Every ${lower} data role`;
    return `${prefix} ${tail}`;
  }

  const prefix = isRemote ? `Every remote ${name} role` : `Every ${name} role`;
  return `${prefix} ${tail}`;
}

const CATEGORY_DESCRIPTIONS: Record<
  string,
  (name: string, count: number) => string
> = {
  positions: (name, count) =>
    `Browse ${count}+ ${name} positions. Find your next ${name} role on Data First Jobs.`,
  "position-group": (name, count) =>
    `Browse ${count}+ ${name} jobs. Find ${name.toLowerCase()} roles across data teams on Data First Jobs.`,
  skills: (name, count) =>
    `${count}+ data jobs requiring ${name}. Filter by ${name} skill and find your ideal position.`,
  "cloud-stack": (name, count) =>
    `${count}+ data jobs on ${name}. Find data engineering and analytics roles using ${name}.`,
  "seniority-level": (name, count) =>
    `${count}+ ${name} data jobs. Browse ${smartLower(name)} positions in data engineering, analytics, and data science.`,
  industry: (name, count) => {
    if (name === "Other") {
      return `${count}+ data jobs across industries. Browse roles on Data First Jobs.`;
    }
    return `${count}+ data jobs in the ${name} industry. Find data roles at ${name.toLowerCase()} companies.`;
  },
  "job-type": (name, count) =>
    `${count}+ ${name.toLowerCase()} data jobs. Browse ${name.toLowerCase()} positions in data engineering and analytics.`,
  "work-options": (name, count) =>
    `${count}+ ${name.toLowerCase()} data jobs. Find ${name.toLowerCase()} roles in data engineering, analytics, and data science.`,
};

/** Meta description — adjusted per category with zero-count handling. */
export function getTagMetaDescription(
  tag: TagForCopy,
  count: number,
  options: CopyOptions = {},
): string {
  const name = getTagDisplayName(tag);
  const isRemote = !!options.isRemote;

  if (count === 0) {
    // "0+ X jobs" reads poorly; swap to a forward-looking sentence.
    // Remote variant emphasizes "fully remote / work from anywhere" so
    // /remote/<slug> and /<slug> read as distinct promises in SERPs.
    return isRemote
      ? `Fully remote ${name.toLowerCase()} jobs — work from anywhere, no on-site required. New remote-only data engineering, analytics, and data science listings added daily.`
      : `${name} jobs on Data First Jobs — new listings added daily. Browse data engineering, analytics, and data science roles.`;
  }

  if (isRemote) {
    // Remote variant: lean into "fully remote / work from anywhere" so the
    // /remote/<slug> meta description differs meaningfully from the
    // /<slug> meta description for the same tag (which uses
    // category-specific templates below).
    return `Browse ${count}+ fully remote ${name.toLowerCase()} jobs — work from anywhere. Remote-only ${name.toLowerCase()} roles across data engineering, analytics, and data science teams.`;
  }

  const catSlug = tag.category?.slug;
  const template = catSlug ? CATEGORY_DESCRIPTIONS[catSlug] : undefined;
  if (template) return template(name, count);
  return `Browse ${count}+ ${name} jobs. Find your next role on Data First Jobs.`;
}

/** Empty-state copy shown on the tag page when initialTotal === 0. */
export function getTagEmptyStateCopy(
  tag: TagForCopy,
  options: CopyOptions = {},
): string {
  const name = getTagDisplayName(tag);
  const lower = smartLower(name);
  if (options.isRemote) {
    return `No remote ${lower} jobs available right now. New positions are added daily — sign up above to get notified when remote ${lower} jobs are posted.`;
  }
  return `No ${lower} jobs available right now. New positions are added daily — sign up above to get notified when ${lower} jobs are posted.`;
}
